import nearestPointOnLine from '@turf/nearest-point-on-line';
import {
  addSnappingVertexLayer,
  clearSnapCoord,
  GeoJsonPoint,
  getSnapToFraction,
} from './utilities';
import { LAYERS } from '@/features/heat-project/constants';
import { SNAP_RADIUS_KM, SET_PROPERTIES } from '@/features/draw/properties';
import { modes } from '@/features/draw/constants';
import MapboxGlDraw from '@mapbox/mapbox-gl-draw';

const snapMode = Object.assign({}, MapboxGlDraw.modes.draw_line_string);

snapMode.SNAP_VERTEX_LAYER_ID = '_snap_vertex';
snapMode.TYPE = LAYERS.DISTRIBUTION_NETWORK;

snapMode.onSetup = function (opts) {
  // bind context to "parent" setup
  const onSetup = MapboxGlDraw.modes.draw_line_string.onSetup.bind(this, opts);
  const state = onSetup();

  this.map.fire(MapboxGlDraw.constants.events.MODE_CHANGE, {
    mode: modes.DRAW_DISTRIBUTION_NETWORK,
  });
  addSnappingVertexLayer(this.map, this.SNAP_VERTEX_LAYER_ID);
  state.snapPoint = GeoJsonPoint([], {});
  return state;
};

snapMode.clickAnywhere = function (state, e) {
  if (
    (state.currentVertexPosition > 0 &&
      MapboxGlDraw.lib.isEventAtCoordinates(
        e,
        state.line.coordinates[state.currentVertexPosition - 1],
      )) ||
    (state.direction === 'backwards' &&
      MapboxGlDraw.lib.isEventAtCoordinates(
        e,
        state.line.coordinates[state.currentVertexPosition + 1],
      ))
  ) {
    return this.changeMode(MapboxGlDraw.constants.modes.SIMPLE_SELECT, {
      featureIds: [state.line.id],
    });
  }
  this.updateUIClasses({ mouse: MapboxGlDraw.constants.cursors.ADD });

  let lng = e.lngLat.lng;
  let lat = e.lngLat.lat;

  const snapVertexCoords = state.snapPoint.geometry.coordinates;
  if (snapVertexCoords.length) {
    [lng, lat] = snapVertexCoords;
  }

  state.line.updateCoordinate(state.currentVertexPosition, lng, lat);

  if (state.direction === 'forward') {
    state.currentVertexPosition++;
    state.line.updateCoordinate(state.currentVertexPosition, lng, lat);
  } else {
    state.line.addCoordinate(0, e.lngLat.lng, e.lngLat.lat);
  }
};

snapMode.onMouseMove = function (state, e) {
  const mousePointer = {
    type: 'Point',
    coordinates: Object.values(e.lngLat),
  };

  const { line } = state;
  const { snapPoint } = state;

  snapPoint.geometry.coordinates = [];

  const updateCoordinate = (lng, lat) => {
    line.updateCoordinate(state.currentVertexPosition, lng, lat);
    if (MapboxGlDraw.lib.CommonSelectors.isVertex(e)) {
      this.updateUIClasses({ mouse: MapboxGlDraw.constants.cursors.POINTER });
    }
  };

  const lineFeatures = this.map.queryRenderedFeatures({
    layers: ['gl-draw-line-deactive.cold'],
    filter: ['==', ['get', 'user_type'], LAYERS.DISTRIBUTION_NETWORK],
  });

  const lineToSnap = getSnapToFraction(lineFeatures, line);

  if (lineToSnap.geometry.coordinates.length === 0) {
    updateCoordinate(e.lngLat.lng, e.lngLat.lat);
  } else {
    const snap = nearestPointOnLine(lineToSnap, mousePointer);
    if (
      snap.properties.dist !== Infinity &&
      snap.properties.dist < SNAP_RADIUS_KM.radius
    ) {
      const [lng, lat] = snap.geometry.coordinates;
      updateCoordinate(lng, lat);
      snapPoint.geometry.coordinates = [lng, lat];
    } else {
      updateCoordinate(e.lngLat.lng, e.lngLat.lat);
    }
    this.map.getSource(this.SNAP_VERTEX_LAYER_ID).setData(snapPoint);
  }
};

snapMode.onKeyUp = function (state, e) {
  if (MapboxGlDraw.lib.CommonSelectors.isEnterKey(e)) {
    this.changeMode(MapboxGlDraw.constants.modes.SIMPLE_SELECT, {
      featureIds: [state.line.id],
    });
  } else if (MapboxGlDraw.lib.CommonSelectors.isEscapeKey(e)) {
    this.deleteFeature([state.line.id], { silent: true });
    this.changeMode(MapboxGlDraw.constants.modes.SIMPLE_SELECT);
  }
};

snapMode.onStop = function (state) {
  clearSnapCoord(this.map, this.SNAP_VERTEX_LAYER_ID);
  MapboxGlDraw.lib.doubleClickZoom.enable(this);
  this.activateUIButton();

  // check to see if we've deleted this feature
  if (this.getFeature(state.line.id) === undefined) return;

  //remove last added coordinate
  state.line.removeCoordinate(`${state.currentVertexPosition}`);
  if (state.line.isValid()) {
    for (const [key, value] of Object.entries(SET_PROPERTIES)) {
      state.line.setProperty(key, value);
    }
    state.line.setProperty('type', this.TYPE);
    this.map.fire(MapboxGlDraw.constants.events.CREATE, {
      features: [state.line.toGeoJSON()],
    });
  } else {
    this.deleteFeature([state.line.id], { silent: true });
    this.changeMode(
      MapboxGlDraw.constants.modes.SIMPLE_SELECT,
      {},
      { silent: true },
    );
  }
};

snapMode.onTrash = function (state) {
  this.deleteFeature([state.line.id], { silent: true });
  this.changeMode(MapboxGlDraw.constants.modes.SIMPLE_SELECT);
};

export default snapMode;
